// Household-360 contracts (AC4), mirrored from the API. The 360 is a read-heavy
// aggregation — the hub the queue / recommendation / claims / commission surfaces
// link through. Everything is rendered as the API returns it (masked reads,
// no new PII surface).

export interface Household {
  id: string;
  name: string;
  relationshipOwnerAdvisorId: string;
  /** U2-B — the resolved advisor display name (staff, not masked). Shown in place of the
   *  raw UUID when present; absent (older payloads / not-yet-deployed) → UUID fallback. */
  relationshipOwnerAdvisorName?: string | null;
  ownerSince: string;
}

/** GET …/ownership → the household + its reassignment history. */
export interface OwnershipView {
  household: Household;
  history: unknown[];
}

/** GET …/members → the masked-free party summary (no TCKN). */
export interface Member {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  status: string;
  createdAt: string;
}

export interface Policy {
  id: string;
  lineCode: string;
  carrierId: string;
  carrierPolicyNumber: string | null;
  termStart: string;
  termEnd: string;
  status: string;
}

export interface Intent {
  id: string;
  intentType: string;
  lawfulBasis: string;
  consentState: string;
  iysApplicable: boolean;
}

export interface Interaction {
  id: string;
  channel: string;
  direction: string;
  occurredAt: string;
  intents: Intent[];
}

export type LifeEventSensitivity = "STANDARD" | "CARE";

export interface LifeEvent {
  id: string;
  eventType: string;
  sensitivity: LifeEventSensitivity;
  status: string;
  occurredAt: string;
}

export interface Consent {
  id: string;
  partyId: string;
  purposeId: string;
  channel: string;
  status: string;
  grantedAt: string;
  withdrawnAt: string | null;
}

// ── CONSOLE-WIRE-3 — the wired panels + surfaces ──

/** GET …/opportunities — no title column; the label comes from opportunityType + detail. */
export interface Opportunity {
  id: string;
  opportunityType: string; // UNINSURED_VEHICLE | KASKO_GAP | LAPSED_NOT_RENEWED
  status: string; // OPEN | ACTIONED | EXPIRED | SUPPRESSED
  detectedAt: string;
  expiresAt: string;
}

/** GET …/renewal-schedule. */
export interface RenewalScheduleItem {
  id: string;
  policyId: string;
  termEnd: string;
  currentStage: string; // T_MINUS_45 … POST_EXPIRY
  status: string; // ACTIVE | COMPLETED | LAPSED
  nextActionAt: string;
}

/** GET …/commission — each entry carries a service-computed `gap` (expected − received). */
export interface CommissionEntry {
  id: string;
  policyId: string;
  carrierId: string;
  expectedAmount: string;
  receivedAmount: string;
  status: string; // EXPECTED | RECONCILED | VARIANCE | DISPUTED | CLAWED_BACK
  gap: string;
}
export interface HouseholdCommission {
  householdId: string;
  entries: CommissionEntry[];
}

/** GET …/engagement — a cheap summary (complements, does not replace, the interactions list). */
export interface Engagement {
  crossSellTouches30d: number;
  lastContactAt: string | null;
}

/** #260 — one entry of the server-composed household timeline. Masked by
 *  construction: `code`/`status` are enums or line codes, never free text. A CARE
 *  life-event arrives toned `care` so the surface renders compassion, not a lead. */
export type TimelineKind = "INTERACTION" | "LIFE_EVENT" | "RECOMMENDATION" | "CLAIM" | "POLICY";
export interface TimelineEntry {
  kind: TimelineKind;
  id: string;
  at: string;
  code: string | null;
  status: string | null;
  tone: "neutral" | "care";
}

/** GET /v1/advisors/me/book — the raw household rows the advisor owns (no advisor-name join). */
export interface BookHousehold {
  id: string;
  name: string;
  relationshipOwnerAdvisorId: string;
  ownerSince: string;
}

/** The server-side filter tabs for the Müşterilerim book (mirrors validation's BookFilter). */
export type BookFilter = "mine" | "action-pending" | "care-hold";

/** GET /v1/advisors/me/book/summary — one book card. MASKED-ONLY: `phone` is the
 *  server's `maskTail(4)` output — the full number NEVER reaches the client (PII-in-DOM
 *  doctrine). Every derived signal is honest-empty (a false flag / 0 count / null hint). */
export interface BookHouseholdSummary {
  householdId: string;
  name: string;
  memberCount: number;
  /** MASKED already (never the full number); null when no member phone. */
  phone: string | null;
  policyCount: number;
  /** The dominant policy 5-status (ACTIVE | CANCELLATION_REQUESTED | CANCELLED | RENEWED |
   *  EXPIRED), or null when the household has no policies. */
  dominantPolicyStatus: string | null;
  renewalDue: boolean;
  careHold: boolean;
  actionPending: boolean;
  /** A lightweight derived next-action hint (or null — honest-empty). */
  nextAction: string | null;
}

/** GET /v1/advisors/me/book/summary — the book envelope: the count ("N hane") + the cards. */
export interface BookSummaryView {
  count: number;
  filter: BookFilter;
  households: BookHouseholdSummary[];
}

// POST /v1/life-events (sensitivity is server-derived; the client never sends it).
export const LIFE_EVENT_TYPES = [
  "NEW_VEHICLE",
  "CHILD_BIRTH",
  "NEW_DEPENDENT",
  "HOME_PURCHASE",
  "MARRIAGE",
  "BEREAVEMENT",
  "ILLNESS",
  "INJURY",
] as const;
export type LifeEventType = (typeof LIFE_EVENT_TYPES)[number];
export interface CreateLifeEventInput {
  householdId: string;
  eventType: LifeEventType;
  source: "ADVISOR_OBSERVED";
  occurredAt?: string;
}

// Ownership actions. reassign-owner → household:reassign@all (admin); participation → any
// authenticated staff (self-recorded, no grant gate).
export const PARTICIPATION_FUNCTIONS = [
  "OPERATIONS",
  "CLAIMS",
  "RENEWAL",
  "SPECIALIST",
  "AFTER_HOURS",
  "OTHER",
] as const;
export type ParticipationFunction = (typeof PARTICIPATION_FUNCTIONS)[number];
export interface ReassignOwnerInput {
  toAdvisorId: string;
  reason: string;
}
export interface ParticipationInput {
  function: ParticipationFunction;
  taskContext: string;
  taskRef?: string;
}

// ── log-interaction (the one in-scope action) ──
export const INTERACTION_CHANNELS = [
  "PHONE",
  "EMAIL",
  "SMS",
  "WHATSAPP",
  "IN_PERSON",
  "PORTAL",
] as const;
export type InteractionChannel = (typeof INTERACTION_CHANNELS)[number];
export const INTERACTION_DIRECTIONS = ["INBOUND", "OUTBOUND"] as const;
export type InteractionDirection = (typeof INTERACTION_DIRECTIONS)[number];

// Relationship intents need no consent; CROSS_SELL is the marketing intent whose
// consent result the UI surfaces honestly.
export const INTENT_TYPES = ["RENEWAL", "SERVICE", "CROSS_SELL", "WINBACK"] as const;
export type IntentType = (typeof INTENT_TYPES)[number];
export const MARKETING_INTENTS: IntentType[] = ["CROSS_SELL", "WINBACK"];

export const CONSENT_STATES = [
  "NOT_REQUIRED",
  "GRANTED",
  "CAPTURED_IN_INTERACTION",
  "ABSENT_LOGGED_OPPORTUNITY",
] as const;
export type ConsentStateInput = (typeof CONSENT_STATES)[number];

export interface LogInteractionInput {
  householdId: string;
  partyId?: string;
  channel: InteractionChannel;
  direction: InteractionDirection;
  intents: Array<{ intentType: IntentType; initiator: "ADVISOR"; consentState: ConsentStateInput }>;
}
